import { pushDL } from "@/lib/analytics";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check } from "lucide-react";
import { getSubscriptionPlans, createCheckoutSession } from "../api/subscription";

import { isGuestUser, getGuestExamUser, isLoggedIn } from "../utils/storage";
import { isValidlyAuthenticated } from "../utils/auth";
import { GoGraph } from "react-icons/go";
import { FaLightbulb } from "react-icons/fa";
import { MdNoteAlt } from "react-icons/md";
import { FaRocket } from "react-icons/fa";
import "./Pricing.css";
import { CheckIcon, StarIcon, ArrowIcon, InfoIcon } from "@/assets/icons";
import { Menu, X } from "lucide-react";
import { HiMenuAlt1 } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import Footer from "@/components/Footer"
import mapleAnimation from "../assets/lottie/canadian-maple-leaf.json";
import Lottie from "lottie-react";
;

function MobileMenuButton({ isOpen, onToggle }) {
  const handleClick = () => {
    onToggle?.();
  };

  return (
    <button aria-label="Toggle menu"
      className={`menu-button ${isOpen ? "open" : ""}`}
      onClick={handleClick}
    >
      <span className="bar"></span>
      <span className="bar"></span>
      <span className="bar"></span>

    </button>
  );
}


interface Price {
  id: string;
  currency: string;
  unit_amount: number;
  formatted_amount: string;
  recurring: {
    interval: string;
    interval_count: number;
  } | null;
  interval: string | null; // fallback interval property
}

interface Product {
  id: string;
  name: string;
  description: string | null;
  metadata?: {
    days?: string;
  };
  prices: Price[];
}

const PriceIframe = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);
  const [guestUserId, setGuestUserId] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [startSpeakingUrl, setStartSpeakingUrl] = useState("/mic-test");
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const isMobile = window.innerWidth <= 768;
  const [loadingPriceId, setLoadingPriceId] = useState<string | null>(null);

  const navigate = useNavigate();
  useEffect(() => {
    const loggedIn = isLoggedIn();
    setIsUserLoggedIn(loggedIn);
    // Trial is guest-only: route the CTA by expiry-aware auth, not token
    // presence. A validly authenticated user goes to Dashboard; a logged-out
    // visitor OR one holding only a stale/expired token goes to the guest Trial.
    if (isValidlyAuthenticated()) {
      setStartSpeakingUrl("/dashboard");
    } else {
      setStartSpeakingUrl("/mic-test?mode=trial");
    }
    // Add a small delay to ensure localStorage is ready
    const checkGuestStatus = () => {
      // Check if user is a guest
      const guestStatus = isGuestUser();
      console.log("🔍 Guest status check:", { guestStatus });

      setIsGuest(guestStatus);

      if (guestStatus) {
        const guestData = getGuestExamUser();
        console.log("🔍 Guest data:", guestData);
        setGuestUserId(guestData?.user_id || null);
      } else {
        console.log("🔍 Not a guest user, checking localStorage:");
        console.log("🔍 guestExamUser:", localStorage.getItem("guestExamUser"));
        console.log("🔍 access_token:", localStorage.getItem("access_token"));
      }
    };

    // Check immediately
    checkGuestStatus();

    // Also check after a small delay in case of timing issues
    setTimeout(checkGuestStatus, 100);

    async function fetchPlans() {
      setLoading(true);
      try {
        const res = await getSubscriptionPlans();
        const sortedProducts = res.products.sort((a, b) => {
          const priceA = a.prices[0]?.unit_amount || 0;
          const priceB = b.prices[0]?.unit_amount || 0;
          return priceA - priceB;
        });
        setProducts(sortedProducts);
      } catch (error) {
        console.error("Error fetching subscription plans:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchPlans();
  }, []);

  async function handleCheckout(priceId: string) {
    if (loadingPriceId) return;
    
    // 🚀 Safari Fix: Open window immediately on user click
    const newWindow = window.open("", "_blank");
    
    setLoadingPriceId(priceId);
    try {
      console.log("🚀 Creating checkout session for price:", priceId);
      // ✅ Analytics only — wrapped so it can NEVER break checkout
      try {
        const planName =
          products.find((p) => p.prices?.some((pr) => pr.id === priceId))?.name || "unknown";
      
        const source =
          sessionStorage.getItem("p2p_checkout_source") || "pricing_page";
      
        pushDL({
          event: "begin_checkout",
          plan_name: planName || "unknown",
          price_id: priceId,
          source: sessionStorage.getItem("p2p_checkout_source") || "pricing_page",
          page_path: window.location.pathname,
          page_location: window.location.href,
        });
      } catch (err) {
        console.error("❌ Analytics push failed (begin_checkout):", err);
      }

      console.log("👤 Guest user status:", { isGuest, guestUserId });
      console.log("🔍 Full localStorage state:");
      console.log("  - guestExamUser:", localStorage.getItem("guestExamUser"));
      console.log("  - access_token:", localStorage.getItem("access_token"));
      console.log("  - session_id:", localStorage.getItem("session_id"));

      if (isGuest && !guestUserId) {
        console.error("❌ Guest user but no guest user ID found!");
        alert("Error: Guest user ID not found. Please refresh the page and try again.");
        setLoadingPriceId(null);
        if (newWindow) newWindow.close(); // Close the blank window
        return;
      }

      console.log("📤 Calling createCheckoutSession with:", {
        priceId,
        isGuest,
        guestUserId: guestUserId || undefined
      });

      const res = await createCheckoutSession(
        priceId,
        `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        `${window.location.origin}/payment-canceled`,
        isGuest,
        guestUserId || undefined
      );

      console.log("✅ Checkout session created, redirecting to:", res.checkout_url);
      
      // 🚀 Safari Fix: Update the already opened window location
      if (newWindow) {
        newWindow.location.href = res.checkout_url;
      } else {
        // Fallback if window.open failed (popup blocker?)
        window.location.href = res.checkout_url;
      }

      // Keep loading state while redirecting
      setTimeout(() => setLoadingPriceId(null), 5000);
      // ✅ Analytics only — prevents stale attribution
      try {
        sessionStorage.removeItem("p2p_checkout_source");
      } catch {}
    } catch (error) {
      console.error("❌ Checkout session creation failed:", error);
      alert("Failed to create checkout session. Please try again.");
      setLoadingPriceId(null);
      if (newWindow) newWindow.close(); // Close the blank window
    }
  }
  const getFeatures = (planName: string) => {
    const features = {
      Light: [
        { text: "Unlimited full speaking exam simulations", Icon: StarIcon },
        { text: "Instant French level (A1–C2) + skill breakdown", Icon: CheckIcon },
        { text: "Personalized feedback + native-level answer", Icon: ArrowIcon },
        { text: "Practice each task individually", Icon: InfoIcon },
      ],
      Pro: [
        { text: "Unlimited full speaking exam simulations", Icon: StarIcon },
        { text: "Instant French level (A1–C2) + skill breakdown", Icon: CheckIcon },
        { text: "Personalized feedback + native-level answer", Icon: ArrowIcon },
        { text: "Practice each task individually", Icon: InfoIcon },
      ],
      Mastery: [
        { text: "Unlimited full speaking exam simulations", Icon: StarIcon },
        { text: "Instant French level (A1–C2) + skill breakdown", Icon: CheckIcon },
        { text: "Personalized feedback + native-level answer", Icon: ArrowIcon },
        { text: "Practice each task individually", Icon: InfoIcon },
      ],
    };
    return features[planName as keyof typeof features] || [];
  };
  if (loading) {
    return (
      <div className="flex items-center justify-center w-full h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading pricing plans...</p>
        </div>
      </div>
    );
  }

  // Enforce visual order of pricing cards
const PLAN_ORDER = ["Light", "Pro", "Mastery"];

const orderedProducts = [...products].sort((a, b) => {
  return PLAN_ORDER.indexOf(a.name) - PLAN_ORDER.indexOf(b.name);
});


  return (
    <div className="pricing-section">
      <header className="pricing-navbar">
        <div className="navbar-container">
          {/* Left Section */}
          <div className="navbar-left">
            <a
              href="https://prep2pass.ca"
              className="flex items-center cursor-pointer"
            >

              {/* Lottie Animation */}
              <div className={`${isMobile ? 'w-[57px] h-[57px]' : 'w-[55px] h-[55px]'} flex items-center justify-center`}>
                <Lottie
                  animationData={mapleAnimation}
                  loop={true}
                  autoplay={true}
                />
              </div>

              {/* Brand name */}
              <a
                className="text-white font-bold leading-none prep-logo"               
              >
                Prep
                <span className="text-span-3">2</span>
                Pass
              </a>

            </a>
          </div>

          {/* Navigation */}
          <nav
            className={`nav-menu-wrapper-2 w-nav-menu  ${menuOpen ? "open" : ""}`}
          >
            <ul role="list" className="nav-menu-two-2 w-list-unstyled">
              <li className="list-item-2">
                <a
                  href="https://prep2pass.ca/#why-it-works"
                  className="nav-link-2 voir-les-formules hover:underline hover:underline-offset-4"
                >
                  Why It Works
                </a>
              </li>
              <li className="list-item-3">
                <a
                  href="/plans"
                  className="nav-link-2 voir-les-formules hover:underline hover:underline-offset-4"
                >
                  Pricing
                </a>
              </li>
              <li className="list-item">
                <a
                  href="https://prep2pass.ca/#faq"
                  className="nav-link-2 voir-les-formules hover:underline hover:underline-offset-4"
                >
                  FAQ
                </a>
              </li>
              <li className="list-item-4">
                <a
                  href={isUserLoggedIn ? "/dashboard" : "https://app.prep2pass.ca/"}
                  className="nav-link-2 voir-les-formules hover:underline hover:underline-offset-4"
                >
                  {isUserLoggedIn ? "Dashboard" : "Sign In"}
                </a>
              </li>
              {/* Free Speaking Exam button */}
              <li className="nav-cta-wrapper" style={{ marginBottom: isMobile ? '10px' : '0px' }}>
                <a
                  href={startSpeakingUrl}
                  className="nav-button"
                >
                  Free Speaking Exam
                </a>
              </li>
            </ul>
          </nav>

          {/* Mobile Menu Button */}
          <div
            className="lg:hidden flex items-center justify-center cursor-pointer z-[1000] " style={{marginTop:"-3px"}}
            onClick={() => {
              if (menuOpen) {
                setClosing(true);
                setTimeout(() => {
                  setMenuOpen(false);
                  setClosing(false);
                }, 100);
              } else {
                setMenuOpen(true);
              }
            }}
          >
            <div className="relative w-7 h-7">
              {/* Hamburger icon */}
              <HiMenuAlt1
                size={35}
                className={`absolute inset-0 transition-all ${menuOpen
                  ? "opacity-0 scale-90 rotate-90 duration-[700ms] ease-in"
                  : "opacity-100 scale-100 rotate-0 duration-[400ms] delay-[500ms] ease-out"
                  } text-white`}
              />

              {/* Close (X) icon */}
              <X
                size={30}
                className={`absolute inset-0 transform transition-all ${menuOpen
                  ? "opacity-100 scale-100 rotate-0 duration-700 ease-out"
                  : "opacity-0 scale-75 -rotate-90 duration-700 ease-in-out"
                  } text-white`}
              />
            </div>
          </div>
        </div>
      </header>
      
        <div className="pricing-container">
          <div className="pricing-header">
            <div className="pricing-header-top">
              <h1 className="pricing-title">Choose your TCF Canada speaking practice plan</h1>
            </div>
        
            <p className="pricing-subtitle">
              One exam simulator. One powerful feedback system.<br />
              You simply choose how long you want access.
            </p>
        
            <p className="pricing-value-note">
              From under $1 CAD/day with Mastery, far cheaper than a private lesson.
            </p>
          </div>
                <div className="pricing-grid">
         {orderedProducts.map((product) => {
            const price = product.prices[0];
            const isPopular = product.name === "Pro";
            const features = getFeatures(product.name);
            const interval = price.recurring?.interval || price.interval || "month";

            return (
              <div
                key={product.id}
                className={`pricing-card ${isPopular ? "popular" : ""}`}
              >
                {isPopular && (
                  <span className="badge badge-popular">
                    Most popular
                  </span>
                )}

                <div className="pricing-card-header">
                  <div className="plan-name">{product.name}</div>

                  <div className="plan-tagline">
                    {product.name === "Light" && "5-day quick prep"}
                    {product.name === "Pro" && "Best for exam month"}
                    {product.name === "Mastery" && "Full month of practice"}
                  </div>

                  <div className="plan-price-box">
                    <div className="price-currency-container">
                      <span className="plan-price">
                        ${(price.unit_amount / 100).toFixed(2)}
                      </span>
                      <span className="plan-currency">
                        {price.currency.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <span className="plan-access">
                        {product.metadata?.days || (product.name === "Light" ? 5 : product.name === "Pro" ? 15 : product.name === "Mastery" ? 30 : 30)} days access
                      </span>
                    </div>
                  </div>
                </div>

                <hr
                  className="pricing-divider"
                />

                <div className="pricing-card-content">
                  <ul className="feature-list">
                    {features.map((featureObj, i) => {
                      const { text, Icon } = featureObj;
                      return (
                        <li key={i} className="feature-item">
                          <Icon className="feature-icon" />
                          <span>{text}</span>
                        </li>
                      );
                    })}
                  </ul>

                  <button
                    className={`choose-btn ${
                      isPopular ? "popular-btn" : "secondary-btn"
                    } ${loadingPriceId === price.id ? "opacity-70 cursor-not-allowed" : ""}`}
                    onClick={() => handleCheckout(price.id)}
                    disabled={!!loadingPriceId}
                  >
                    {loadingPriceId === price.id ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </span>
                    ) : (
                      "Unlock Full Access"
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        {/* <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-sm font-semibold text-yellow-800">Debug Info:</p>
            <p className="text-sm text-yellow-700">Is Guest: {isGuest ? "Yes" : "No"}</p>
            <p className="text-sm text-yellow-700">Guest User ID: {guestUserId || "None"}</p>
            <button 
              onClick={() => {
                console.log("Manual test - isGuestUser():", isGuestUser());
                console.log("Manual test - getGuestExamUser():", getGuestExamUser());
                console.log("Manual test - localStorage:", localStorage.getItem("guestExamUser"));
              }}
              className="mt-2 px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
            >
              Test Guest Detection
            </button>
          </div> */}
      </div>
      <Footer />
    </div>

  );
};

export default PriceIframe;


